import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Plus, Search, X } from 'lucide-react';
import { EMPTY_FILTER, applySuggestion, isFilterActive, parseQuery, toggleSource, type LibraryFilter } from '@/lib/library/search';
import { sourceMeta, type LibrarySource } from '@/lib/library/sources';
import { cn } from '@/lib/utils';
import { SourceGlyph } from './SourceGlyph';
import { NoteGlyph } from './LibraryIcons';

export interface LibrarySearchBarProps {
  filter: LibraryFilter;
  onFilterChange: (filter: LibraryFilter) => void;
  /** Sources present in the user's library, in display order: the chips to offer. */
  availableSources: LibrarySource[];
  /** Number of cards matching the current filter. */
  resultCount: number;
  /** Told when the field gains or loses focus, so the shell can slide the tab bar away. */
  onFocusChange?: (focused: boolean) => void;
  /** The faint pencil at the end of the resting pill: write a note. */
  onWriteNote?: () => void;
}

const SPRING = { type: 'spring', stiffness: 500, damping: 38 } as const;
const INSTANT = { duration: 0 } as const;
/** Space between the chips and the top of the keyboard. */
const KEYBOARD_GAP = 10;
/** Chip row height plus its gap under the pill, before it is measured. */
const CHIPS_ESTIMATE = 48;

// The last keyboard height seen, so the next focus can rise with the keyboard
// instead of waiting for Safari to report it at the end of its animation
let lastKeyboard = 0;
function keyboardGuess(): number {
  if (lastKeyboard) return lastKeyboard;
  try {
    const stored = Number(localStorage.getItem('library-keyboard-height'));
    if (stored > 100) return (lastKeyboard = stored);
  } catch {
    // storage blocked: fall through to the estimate
  }
  const touch = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
  return touch ? Math.round(window.innerHeight * 0.42) : 0;
}
function rememberKeyboard(height: number) {
  if (height < 100 || Math.abs(height - lastKeyboard) < 2) return;
  lastKeyboard = height;
  try {
    localStorage.setItem('library-keyboard-height', String(height));
  } catch {
    // fine: the in-memory value still serves this session
  }
}

/**
 * The floating search pill above the tab bar, after mymind's mobile search.
 *
 * Focused, the tab bar slides away and the pill rises in one move to sit
 * above the keyboard, with a row of source chips under it. Everything moves
 * by transform, nothing reflows, and the rise starts from the last keyboard
 * height seen so it travels with the keyboard. The page dims while you pick
 * a filter and clears as soon as you type, so results stay visible.
 *
 * Chips toggle `filter.sources`; words like "reels" or "pdfs" in the query
 * put their chip first, marked with a plus, and tapping it swaps the word for
 * the filter. Nothing filters on a guessed word by itself. Tapping outside
 * or the keyboard's search key closes the keyboard and keeps the filter.
 * Blurred with a filter on, the pill shows the query (or the chosen chips),
 * the result count and a clear button for the whole filter; with nothing on,
 * it ends in a faint pencil that starts a note.
 */
export function LibrarySearchBar({ filter, onFilterChange, availableSources, resultCount, onFocusChange, onWriteNote }: LibrarySearchBarProps) {
  const reduce = useReducedMotion();
  const inputRef = useRef<HTMLInputElement>(null);
  // iPhone: the tap lands on this field at the top of the screen first, so the
  // keyboard opens without Safari scrolling the page to reveal a field at the
  // bottom; the pill's own field takes over once the pill has risen
  const proxyRef = useRef<HTMLInputElement>(null);
  const touch = useMemo(() => typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches, []);
  // The visible part of the page while the keyboard is up: Safari overlays the
  // keyboard and may pan, so both the height and the pan count
  const [viewport, setViewport] = useState<{ top: number; height: number } | null>(null);
  const pillRef = useRef<HTMLFormElement>(null);
  const chipsRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);
  // Distance from the resting pill's bottom edge to the bottom of the screen
  // Where the resting pill's bottom edge sits on the page, measured before it moves
  const [restBottom, setRestBottom] = useState(0);
  const [chipsHeight, setChipsHeight] = useState(CHIPS_ESTIMATE);
  // Keeps the tap-catcher alive until the tap that closed the keyboard ends,
  // so the same tap can't land on a tile underneath and open it
  const [catching, setCatching] = useState(false);
  const catchTimer = useRef<number>();

  useEffect(() => {
    onFocusChange?.(focused);
  }, [focused, onFocusChange]);

  useEffect(() => () => window.clearTimeout(catchTimer.current), []);

  // Follow the visual viewport only while searching
  useEffect(() => {
    const vv = window.visualViewport;
    if (!focused || !vv) {
      setViewport(null);
      return;
    }
    const update = () => {
      const keyboardUp = vv.height < window.innerHeight - 120 && vv.scale < 1.01;
      setViewport(keyboardUp ? { top: vv.offsetTop, height: vv.height } : null);
      if (keyboardUp) rememberKeyboard(window.innerHeight - vv.height);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [focused]);

  // Chips render below the pill out of flow; their height sets how high the pill rises
  useLayoutEffect(() => {
    if (!focused) return;
    const h = chipsRef.current?.offsetHeight;
    if (h) setChipsHeight(h + 10);
  }, [focused, availableSources.length]);

  const parsed = useMemo(() => parseQuery(filter.query), [filter.query]);
  const available = useMemo(() => new Set(availableSources), [availableSources]);
  const suggestions = parsed.suggestedSources.filter((s) => available.has(s.source) && !filter.sources.includes(s.source));
  const suggested = new Set(suggestions.map((s) => s.source));
  const otherChips = availableSources.filter((s) => !suggested.has(s));

  const active = isFilterActive(filter);
  const hasQuery = filter.query.trim().length > 0;
  const chipLabels = filter.sources.map((s) => sourceMeta(s).label).join(', ');
  const showChipLabels = !focused && !hasQuery && filter.sources.length > 0;
  const transition = reduce ? INSTANT : SPRING;
  const hasChips = availableSources.length > 0;

  // Focused: lift the pill so the pill and chips end just above the keyboard
  // The bottom of what you can see: from the visual viewport once Safari
  // reports the keyboard, from the last keyboard height until then. Measured
  // in page coordinates, so a pan Safari makes anyway is already counted.
  const visibleBottom = viewport ? viewport.top + viewport.height : window.innerHeight - keyboardGuess();
  const lift = focused && touch ? visibleBottom - KEYBOARD_GAP - (hasChips ? chipsHeight : 0) - restBottom : 0;
  // Dim the page while choosing what to search; clear it once a word or chip is in
  const dim = focused && !active;

  const blur = () => {
    const active = document.activeElement;
    if (active === inputRef.current || active === proxyRef.current) (active as HTMLElement).blur();
  };

  /** Measure, then open: the shared start for a tap (proxy) and a click or keyboard focus (pill field). */
  const measureRest = () => {
    const rect = pillRef.current?.getBoundingClientRect();
    if (rect && !focused) setRestBottom(rect.bottom);
  };
  const openFromTap = () => {
    measureRest();
    setFocused(true);
    proxyRef.current?.focus({ preventScroll: true });
  };
  // Either field losing focus to the other is not a blur of the search
  const handleBlur = () => {
    window.setTimeout(() => {
      const active = document.activeElement;
      if (active !== inputRef.current && active !== proxyRef.current) setFocused(false);
    }, 0);
  };
  // Once the pill has arrived above the keyboard, typing moves into its own field
  // (on animation complete; this timer covers a pill that had nowhere to move)
  useEffect(() => {
    if (!focused || !touch) return;
    const t = window.setTimeout(() => handOverRef.current(), 480);
    return () => window.clearTimeout(t);
  }, [focused, touch, viewport]);
  const handOverRef = useRef<() => void>(() => {});
  const handOver = () => {
    const proxy = proxyRef.current;
    const input = inputRef.current;
    if (!proxy || !input || document.activeElement !== proxy) return;
    input.focus({ preventScroll: true });
    const end = input.value.length;
    try {
      input.setSelectionRange(end, end);
    } catch {
      // search inputs may refuse selection ranges; the caret lands at the end anyway
    }
  };
  useEffect(() => {
    handOverRef.current = handOver;
  });

  const clear = () => {
    if (focused) onFilterChange({ ...filter, query: '' });
    else onFilterChange(EMPTY_FILTER);
  };

  return (
    <>
      <AnimatePresence>
        {(focused || catching) && (
          <motion.div
            key="scrim"
            aria-hidden
            className="pointer-events-auto fixed inset-0 bg-paper"
            initial={{ opacity: 0 }}
            animate={{ opacity: dim ? 0.82 : 0 }}
            exit={{ opacity: 0 }}
            transition={reduce ? INSTANT : { duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            onPointerDown={() => {
              setCatching(true);
              blur();
              window.clearTimeout(catchTimer.current);
              catchTimer.current = window.setTimeout(() => setCatching(false), 600);
            }}
            onClick={() => setCatching(false)}
          />
        )}
      </AnimatePresence>
      {touch && (
        <input
          ref={proxyRef}
          type="search"
          inputMode="search"
          enterKeyHint="search"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          tabIndex={-1}
          aria-hidden
          value={filter.query}
          onChange={(e) => onFilterChange({ ...filter, query: e.target.value })}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Escape') {
              e.preventDefault();
              blur();
            }
          }}
          // 16px so Safari does not zoom; invisible, but on screen at the top so focusing it never scrolls the page
          className="pointer-events-none fixed left-0 top-0 h-px w-px opacity-0"
          style={{ fontSize: 16 }}
        />
      )}
      <motion.div
        className="relative flex flex-col"
        initial={false}
        animate={{ y: lift }}
        transition={transition}
        onAnimationComplete={() => {
          if (focused) handOver();
        }}
      >
        <form
          ref={pillRef}
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            blur();
          }}
          className={cn(
            // Glass like the desktop's floating pill, a little taller than a field so it reads as the way in
            'pointer-events-auto mx-4 flex h-14 items-center gap-2.5 rounded-full border bg-paper-raised/80 pl-5 pr-2 shadow-float backdrop-blur-[24px] backdrop-saturate-[1.4] transition-colors duration-150 ease-out',
            focused ? 'border-signal' : 'border-line',
          )}
        >
          <Search className="h-[18px] w-[18px] shrink-0 text-ink-3" strokeWidth={1.75} aria-hidden />
          <div className="relative flex min-w-0 flex-1 items-center">
            <input
              ref={inputRef}
              type="search"
              inputMode="search"
              enterKeyHint="search"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              value={filter.query}
              onChange={(e) => onFilterChange({ ...filter, query: e.target.value })}
              onTouchEnd={(e) => {
                // A tap on the resting pill opens through the top field instead (see proxyRef)
                if (!touch || focused) return;
                e.preventDefault();
                openFromTap();
              }}
              onFocus={() => {
                measureRest();
                setFocused(true);
              }}
              onBlur={handleBlur}
              onKeyDown={(e) => {
                if (e.key === 'Escape') blur();
              }}
              placeholder={showChipLabels ? '' : 'Search your mind'}
              aria-label={showChipLabels ? `Search your mind, filtered to ${chipLabels}` : 'Search your mind'}
              className="h-11 min-w-0 flex-1 bg-transparent text-[16px] text-ink outline-none placeholder:text-ink-3 [&::-webkit-search-cancel-button]:hidden"
            />
            {showChipLabels && (
              <span aria-hidden className="pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center truncate text-[16px] text-ink">
                <span className="truncate">{chipLabels}</span>
              </span>
            )}
          </div>
          {active && (
            <span className="shrink-0 font-mono text-[12px] tabular-nums text-ink-3" aria-live="polite" aria-label={`${resultCount} ${resultCount === 1 ? 'result' : 'results'}`}>
              {resultCount}
            </span>
          )}
          {(focused ? hasQuery : active) ? (
            <button
              type="button"
              // Keep the keyboard up when clearing while typing
              onMouseDown={(e) => e.preventDefault()}
              onClick={clear}
              aria-label={focused ? 'Clear search text' : 'Clear search and filters'}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ink-2 transition-colors duration-150 ease-out active:bg-wash focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal"
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-wash">
                <X className="h-3.5 w-3.5" strokeWidth={2} />
              </span>
            </button>
          ) : !focused && onWriteNote ? (
            <button
              type="button"
              onClick={onWriteNote}
              aria-label="Write a note"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ink-3 transition-[color,background-color,transform] duration-150 ease-out active:scale-[0.94] active:bg-wash active:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal motion-reduce:active:scale-100"
            >
              <NoteGlyph className="h-[18px] w-[18px]" />
            </button>
          ) : (
            <span className="w-2.5 shrink-0" aria-hidden />
          )}
        </form>

        <AnimatePresence initial={false}>
          {focused && hasChips && (
            <motion.div
              key="chips"
              ref={chipsRef}
              className="pointer-events-auto absolute inset-x-0 top-full pt-2.5"
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? { opacity: 0, transition: INSTANT } : { opacity: 0, y: -6, transition: { duration: 0.12 } }}
              transition={reduce ? INSTANT : { ...SPRING, opacity: { duration: 0.18, delay: 0.06 } }}
            >
              <div
                role="toolbar"
                aria-label="Filter by source"
                // Chips must not steal focus from the field, or the keyboard drops
                onMouseDown={(e) => e.preventDefault()}
                className="flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {suggestions.map((s) => (
                  <SourceChip
                    key={`suggest-${s.source}`}
                    source={s.source}
                    state="suggested"
                    onClick={() => onFilterChange(applySuggestion(filter, s))}
                  />
                ))}
                {otherChips.map((source) => (
                  <SourceChip
                    key={source}
                    source={source}
                    state={filter.sources.includes(source) ? 'selected' : 'idle'}
                    onClick={() => onFilterChange(toggleSource(filter, source))}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
}

function SourceChip({ source, state, onClick }: { source: LibrarySource; state: 'idle' | 'selected' | 'suggested'; onClick: () => void }) {
  const { label } = sourceMeta(source);
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={state === 'selected'}
      aria-label={state === 'suggested' ? `Filter by ${label}, suggested from your search` : `Filter by ${label}`}
      className={cn(
        'flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-[13px] font-medium transition-[background-color,color,border-color,transform] duration-150 ease-out active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal motion-reduce:transition-none motion-reduce:active:scale-100',
        state === 'selected' && 'border border-ink bg-ink text-paper',
        state === 'idle' && 'border border-line bg-paper-raised text-ink-2',
        state === 'suggested' && 'border border-ink bg-paper-raised text-ink',
      )}
    >
      {state === 'suggested' && <Plus className="-ml-0.5 h-3.5 w-3.5" strokeWidth={2} aria-hidden />}
      <SourceGlyph source={source} className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}
